/** Pricing page → "Installer price tiers" card: add, rename, reorder, delete, and set a % off RRP per tier. */
import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Check, Layers, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';

async function call(path, method, body) {
  const r = await fetch(`${API_URL}/api/installer-tiers${path}`, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(d) || 'Request failed');
  return d;
}

const applied = (res) => (res ? ` · ${res.updated} price${res.updated === 1 ? '' : 's'} recalculated${res.no_rrp ? `, ${res.no_rrp} skipped (no RRP)` : ''}` : '');

function TierRow({ t, idx, total, busy, onRename, onDiscount, onMove, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(t.label);
  const [pct, setPct] = useState(t.discount_pct ?? '');
  const dirtyPct = String(pct) !== String(t.discount_pct ?? '');
  return (
    <tr className="border-t border-gray-100" data-testid={`tier-row-${t.slug}`}>
      <td className="px-3 py-2 w-16 whitespace-nowrap">
        <button type="button" disabled={idx === 0 || busy} onClick={() => onMove(idx, -1)} className="p-1 text-gray-400 hover:text-gray-900 disabled:opacity-20" aria-label="Move up" data-testid={`tier-up-${t.slug}`}><ArrowUp className="w-3.5 h-3.5" /></button>
        <button type="button" disabled={idx === total - 1 || busy} onClick={() => onMove(idx, 1)} className="p-1 text-gray-400 hover:text-gray-900 disabled:opacity-20" aria-label="Move down" data-testid={`tier-down-${t.slug}`}><ArrowDown className="w-3.5 h-3.5" /></button>
      </td>
      <td className="px-3 py-2 font-semibold text-gray-900">
        {editing ? (
          <span className="inline-flex items-center gap-1">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 w-40" autoFocus data-testid={`tier-rename-input-${t.slug}`} onKeyDown={(e) => { if (e.key === 'Enter') { onRename(t, label); setEditing(false); } if (e.key === 'Escape') { setLabel(t.label); setEditing(false); } }} />
            <button type="button" onClick={() => { onRename(t, label); setEditing(false); }} className="p-1 text-emerald-700" aria-label="Save name" data-testid={`tier-rename-save-${t.slug}`}><Check className="w-4 h-4" /></button>
            <button type="button" onClick={() => { setLabel(t.label); setEditing(false); }} className="p-1 text-gray-400" aria-label="Cancel" data-testid={`tier-rename-cancel-${t.slug}`}><X className="w-4 h-4" /></button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            {t.label}
            <button type="button" onClick={() => setEditing(true)} className="p-0.5 text-gray-400 hover:text-gray-900" aria-label={`Rename ${t.label}`} data-testid={`tier-rename-${t.slug}`}><Pencil className="w-3.5 h-3.5" /></button>
            {t.slug === 'trade' && <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">default</span>}
          </span>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-gray-500">−</span>
          <Input type="number" step="0.5" min="0" max="100" value={pct} placeholder="manual" onChange={(e) => setPct(e.target.value)} className="h-8 w-24 text-right" data-testid={`tier-discount-${t.slug}`} />
          <span className="text-gray-500">% off RRP</span>
          {dirtyPct && <Button size="sm" variant="outline" disabled={busy} onClick={() => onDiscount(t, pct)} className="h-8 text-xs" data-testid={`tier-discount-apply-${t.slug}`}>{pct === '' ? 'Switch to manual' : 'Apply to all products'}</Button>}
        </span>
      </td>
      <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap" data-testid={`tier-installers-${t.slug}`}>{t.installers ?? 0}</td>
      <td className="px-2 py-2 text-right">
        {t.slug !== 'trade' && <button type="button" disabled={busy} onClick={() => onDelete(t)} className="p-1 text-gray-400 hover:text-rose-600" aria-label={`Delete ${t.label}`} data-testid={`tier-delete-${t.slug}`}><Trash2 className="w-4 h-4" /></button>}
      </td>
    </tr>
  );
}

export function TierManagerCard({ tiers, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newPct, setNewPct] = useState('');

  const run = async (fn, ok) => {
    setBusy(true);
    try { const d = await fn(); onChanged?.(d.tiers); toast.success(typeof ok === 'function' ? ok(d) : ok); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const add = () => {
    if (!newLabel.trim()) return;
    run(() => call('', 'POST', { label: newLabel.trim(), discount_pct: newPct === '' ? null : Number(newPct) }),
      (d) => `${newLabel.trim()} tier added — it now has its own column in the price book${applied(d.result)}`).then(() => { setNewLabel(''); setNewPct(''); });
  };
  const rename = (t, label) => { if (label.trim() && label.trim() !== t.label) run(() => call(`/${t.slug}`, 'PUT', { label: label.trim() }), `Renamed to ${label.trim()}`); };
  const discount = (t, pct) => run(
    () => call(`/${t.slug}`, 'PUT', pct === '' ? { clear_discount: true } : { discount_pct: Number(pct) }),
    (d) => (pct === '' ? `${t.label}: prices are now typed by hand` : `${t.label} = RRP − ${Number(pct)}%${applied(d.result)}`),
  );
  const move = (idx, dir) => {
    const next = [...tiers]; const [row] = next.splice(idx, 1); next.splice(idx + dir, 0, row);
    run(() => call('/reorder', 'POST', { slugs: next.map((t) => t.slug) }), 'Tier order saved');
  };
  const del = (t) => {
    if (!window.confirm(`Delete the ${t.label} tier? Its price column is removed from every product.`)) return;
    run(() => call(`/${t.slug}`, 'DELETE'), `${t.label} tier deleted`);
  };

  return (
    <Card className="p-4 border-2 border-gray-200" data-testid="tier-manager-card">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-gray-500" /><h3 className="font-bold text-gray-900">Installer price tiers</h3>{busy && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}</div>
          <p className="text-xs text-gray-600 mt-1">Add, rename or reorder the tiers installers can be put on — each one is a column in the price book below, for every brand. Give a tier a % off RRP and every product with an RRP is priced from it automatically (those cells lock; products without an RRP stay hand-typed; clear the % to type everything by hand again).</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} className="h-8 text-xs" data-testid="tier-manager-toggle">{open ? 'Hide' : `Manage tiers (${tiers.length})`}</Button>
      </div>
      {open && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid="tier-manager-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
              <tr><th className="px-3 py-2 text-left font-semibold">Order</th><th className="px-3 py-2 text-left font-semibold">Tier</th><th className="px-3 py-2 text-left font-semibold">Pricing rule</th><th className="px-3 py-2 text-right font-semibold">Installers</th><th className="w-10" /></tr>
            </thead>
            <tbody>
              {tiers.map((t, i) => <TierRow key={t.slug} t={t} idx={i} total={tiers.length} busy={busy} onRename={rename} onDiscount={discount} onMove={move} onDelete={del} />)}
              <tr className="border-t-2 border-gray-200 bg-gray-50/60">
                <td className="px-3 py-2 text-gray-400"><Plus className="w-4 h-4" /></td>
                <td className="px-3 py-2"><Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New tier name, e.g. Platinum" className="h-8 w-48" onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="tier-new-label" /></td>
                <td className="px-3 py-2 whitespace-nowrap"><span className="inline-flex items-center gap-1.5"><span className="text-gray-500">−</span><Input type="number" step="0.5" min="0" max="100" value={newPct} placeholder="manual" onChange={(e) => setNewPct(e.target.value)} className="h-8 w-24 text-right" data-testid="tier-new-discount" /><span className="text-gray-500">% off RRP</span></span></td>
                <td />
                <td className="px-2 py-2 text-right"><Button size="sm" disabled={busy || !newLabel.trim()} onClick={add} className="h-8 text-xs" data-testid="tier-add-btn">Add tier</Button></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
